import { useState, useCallback, useRef, useMemo } from "react";
import { Upload, Loader2, FileText, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DOCUMENT_TYPES } from "@/lib/api";
import type { DocumentType } from "@/lib/api";
import {
  usePatientDocuments,
  useUploadPatientDocument,
} from "@/hooks/usePatientDocuments";
import type { PatientDocument } from "@/hooks/usePatientDocuments";
import { DocumentFileCard } from "@/components/consultation/shared/DocumentFileCard";
import { FilePreviewDialog } from "@/components/ui/file-preview";
import { EmptyState } from "@/components/consultation/EmptyState";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const ACCEPTED_TYPES = ".pdf,.jpg,.jpeg,.png,.doc,.docx";
const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB

const UPLOAD_DOC_TYPES: { value: DocumentType; label: string }[] = [
  { value: "LR", label: "Lab Report" },
  { value: "PR", label: "Prescription" },
  { value: "MD", label: "Medicine" },
  { value: "SC", label: "Imaging" },
  { value: "OT", label: "Others" },
];

type FilterTab = "all" | "LR" | "PR" | "MD" | "SC" | "OT";

const FILTER_TABS: { key: FilterTab; label: string }[] = [
  { key: "all", label: "All" },
  { key: "LR", label: "Lab Reports" },
  { key: "SC", label: "Imaging" },
  { key: "MD", label: "Medicines" },
  { key: "PR", label: "Prescriptions" },
  { key: "OT", label: "Others" },
];

interface PatientDocumentsProps {
  patientId: string;
  appointmentId?: string;
}

export const PatientDocuments = ({ patientId, appointmentId }: PatientDocumentsProps) => {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [filterTab, setFilterTab] = useState<FilterTab>("all");
  const [previewDoc, setPreviewDoc] = useState<PatientDocument | null>(null);

  const typeFilter = useMemo((): DocumentType[] => {
    if (filterTab === "all") return [];
    return [filterTab];
  }, [filterTab]);

  const { data: documents = [], isLoading } = usePatientDocuments(patientId, {
    types: typeFilter,
    includeUncategorized: filterTab === "all",
  });

  return (
    <Card className="border-border/60 bg-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="z-body-md text-foreground">Documents</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setUploadOpen(true)}
            disabled={!appointmentId}
            title={!appointmentId ? "No appointments available for upload" : undefined}
          >
            <Upload className="h-3.5 w-3.5 mr-1.5" />
            Upload
          </Button>
        </div>

        <Tabs
          value={filterTab}
          onValueChange={(v) => setFilterTab(v as FilterTab)}
        >
          <TabsList className="h-auto p-0.5 w-fit mb-3">
            {FILTER_TABS.map((tab) => (
              <TabsTrigger
                key={tab.key}
                value={tab.key}
                className="z-caption px-3 py-1 rounded-full"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : documents.length === 0 ? (
          <EmptyState
            title={
              filterTab === "all"
                ? "No documents uploaded"
                : `No ${DOCUMENT_TYPES[filterTab as DocumentType]?.toLowerCase() ?? ""} documents`
            }
            description="Upload documents for this patient using the button above."
            variant="compact"
          />
        ) : (
          <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
            {documents.map((doc) => (
              <DocumentFileCard
                key={doc.id}
                id={doc.id}
                fileUrl={doc.file}
                name={doc.description || ""}
                documentType={doc.document_type}
                compact
                onClick={() => setPreviewDoc(doc)}
              />
            ))}
          </div>
        )}

        {appointmentId && (
          <UploadDocumentDialog
            open={uploadOpen}
            onOpenChange={setUploadOpen}
            appointmentId={appointmentId}
            patientId={patientId}
          />
        )}

        {previewDoc && (
          <FilePreviewDialog
            open={!!previewDoc}
            onOpenChange={(open) => {
              if (!open) setPreviewDoc(null);
            }}
            fileUrl={previewDoc.file}
            fileName={previewDoc.description || "Document"}
            createdAt={previewDoc.created_at}
          />
        )}
      </CardContent>
    </Card>
  );
};

function UploadDocumentDialog({
  open,
  onOpenChange,
  appointmentId,
  patientId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId: string;
  patientId: string;
}) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<DocumentType>("OT");
  const [description, setDescription] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useUploadPatientDocument(appointmentId, patientId);

  const resetForm = useCallback(() => {
    setSelectedFile(null);
    setDocumentType("OT");
    setDescription("");
    setIsDragOver(false);
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) resetForm();
      onOpenChange(open);
    },
    [onOpenChange, resetForm]
  );

  const validateFile = useCallback((file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) return "File size must be under 15MB.";
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    const allowed = ["pdf", "jpg", "jpeg", "png", "doc", "docx"];
    if (!allowed.includes(ext))
      return "Only PDF, JPG, PNG, DOC, DOCX files are allowed.";
    return null;
  }, []);

  const handleFileSelect = useCallback(
    (file: File) => {
      const error = validateFile(file);
      if (error) {
        toast.error(error);
        return;
      }
      setSelectedFile(file);
    },
    [validateFile]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;
    try {
      await uploadMutation.mutateAsync({
        file: selectedFile,
        documentType,
        description: description.trim() || undefined,
      });
      toast.success("Document uploaded");
      handleOpenChange(false);
    } catch {
      toast.error("Failed to upload document");
    }
  }, [
    selectedFile,
    documentType,
    description,
    uploadMutation,
    handleOpenChange,
  ]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* Drop zone / file picker */}
          {!selectedFile ? (
            <div
              className={cn(
                "flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 transition-colors cursor-pointer",
                isDragOver
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/40 hover:bg-muted/30"
              )}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-8 w-8 text-muted-foreground" />
              <div className="text-center">
                <p className="z-body-md text-foreground">
                  Drop file here or click to browse
                </p>
                <p className="z-caption text-muted-foreground mt-1">
                  PDF, JPG, PNG, DOC, DOCX — max 15MB
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelect(file);
                  e.target.value = "";
                }}
              />
            </div>
          ) : (
            <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="z-body-md text-foreground truncate">
                  {selectedFile.name}
                </p>
                <p className="z-caption text-muted-foreground">
                  {(selectedFile.size / 1024).toFixed(0)} KB
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => setSelectedFile(null)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Document type */}
          <div className="flex flex-col gap-1.5">
            <Label className="z-caption">Document Type</Label>
            <Select
              value={documentType}
              onValueChange={(v) => setDocumentType(v as DocumentType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {UPLOAD_DOC_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="flex flex-col gap-1.5">
            <Label className="z-caption">
              Description{" "}
              <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input
              placeholder="e.g. Blood test results from July"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={uploadMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploadMutation.isPending}
            >
              {uploadMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              )}
              Upload
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
